/**
 * Web Space Component
 *
 */

import { useContext } from 'react'
import type { HTMLProps, Ref } from 'react'
import clsx from 'clsx'
import {
  extendPropsWithContext,
  validateDOMAttributes,
} from '../../shared/component-helper'
import type { ContextProps } from '../../shared/Context'
import Context from '../../shared/Context'
import { useSpacing, isInline } from './SpacingUtils'
import {
  skeletonDOMAttributes,
  createSkeletonClass,
} from '../skeleton/SkeletonHelper'

import type {
  DynamicElement,
  DynamicElementParams,
  SpacingProps,
} from '../../shared/types'
import type { SkeletonShow } from '../Skeleton'
import type { InnerSpaceType } from './types'
import withComponentMarkers from '../../shared/helpers/withComponentMarkers'
import SpaceResponsive from './SpaceResponsive'
export type { SpaceResponsiveProps } from './SpaceResponsive'
export type {
  SpaceDensity,
  SpaceBreakpoint,
} from './SpaceResponsiveContext'

export type SpaceProps = {
  /**
   * Defines the HTML element used.
   * Default: `div`
   */
  element?: DynamicElement

  /**
   * If set to `true`, then `display: inline-block;` is used, so the HTML elements get aligned horizontally. Defaults to `false`.
   */
  inline?: boolean

  /**
   * If set to `true`, then a wrapper with `display: flow-root;` is used. This way you avoid **Margin Collapsing**. Defaults to `false`. _Note:_ You can't use `inline={true}` in combination.
   */
  noCollapse?: boolean

  /**
   * If set to `true`, then the space element will be 100% in width.
   * Default: `false`
   */
  stretch?: boolean

  /**
   * If set to `true`, a loading skeleton will be shown.
   * Default: `false`
   */
  skeleton?: SkeletonShow

  /**
   * Send along a custom React Ref.
   * Default: `null`
   */
  ref?: Ref<HTMLElement>
} & Omit<SpacingProps, 'innerSpace'> & { innerSpace?: InnerSpaceType }

export type SpaceAllProps = SpaceProps &
  Omit<HTMLProps<HTMLElement>, 'ref'>

const defaultProps: Partial<SpaceAllProps> = {}

function SpaceInstance(localProps: SpaceAllProps) {
  const context = useContext<ContextProps & SpacingProps>(Context)

  // consume the space context
  const props = context.space
    ? // use only the props from context, who are available here anyway
      extendPropsWithContext(
        localProps,
        defaultProps,
        { space: context.space },
        { skeleton: context?.skeleton }
      )
    : localProps

  const {
    element = 'div',
    inline,
    noCollapse,
    top,
    right,
    bottom,
    left,
    style,
    space,
    innerSpace,
    stretch,
    skeleton,
    ref,
    className,
    children,

    ...attributes
  } = props

  const params = useSpacing(
    { top, right, bottom, left, space, innerSpace },
    {
      style,
      className: clsx(
        'dnb-space',
        stretch && 'dnb-space--stretch',
        inline && 'dnb-space--inline',
        createSkeletonClass(null, skeleton) // do not send along context
      ),
      ...attributes,
    }
  )

  // Append component className after spacing classes to preserve
  // the historical class order: dnb-space, spacing, component classes
  params.className = clsx(params.className, className)

  skeletonDOMAttributes(params, skeleton) // do not send along context

  return (
    <SpaceElement
      element={element}
      noCollapse={noCollapse}
      ref={ref}
      {...params}
    >
      {children}
    </SpaceElement>
  )
}

function Space(props: SpaceAllProps) {
  return <SpaceInstance {...props} />
}

Space.ResponsiveContext = SpaceResponsive

withComponentMarkers(Space, { _supportsSpacingProps: true })
withComponentMarkers(Space.ResponsiveContext, {
  _supportsSpacingProps: 'passthrough',
})

export default Space

function SpaceElement({
  element,
  noCollapse,
  children,
  ref,
  ...props
}: SpaceAllProps) {
  const ElementDynamic = element

  if (typeof element === 'string') {
    // also used for code markup simulation
    validateDOMAttributes({}, props)
  }
  props['ref'] = ref

  const component = (
    <ElementDynamic {...(props as DynamicElementParams)}>
      {children}
    </ElementDynamic>
  )

  if (noCollapse) {
    const R =
      ElementDynamic === 'span' || isInline(element as string)
        ? 'span'
        : 'div'
    return (
      <R
        className={clsx(
          'dnb-space--no-collapse',
          isInline(element as string) && 'dnb-space--inline'
        )}
      >
        {component}
      </R>
    )
  }

  return component
}
